/**
 * Helpers front pour l'affichage des jalons / réception avec séquestre.
 *
 * Aucune logique métier : ce qui fait foi côté serveur reste le
 * ``business_days_deadline_utc`` calculé à l'ouverture du compteur.
 * Ici on se contente de traduire ces deadlines en libellés humains
 * (décompte de jours calendaires restants + phrase de conséquence).
 */

export const JALON_STATUS_LABEL = {
  en_attente: "En attente d'achèvement",
  en_attente_demarrage: "En attente du démarrage",
  demande_validation: "En attente de validation",
  reserves_deposees: "Réserves déposées",
  reserves_ecartees_regul: "Réserves écartées — régularisation",
  reception_acquise: "Réception acquise",
  debloque: "Fonds libérés",
  litige: "Litige",
};

export const JALON_STATUS_CLASS = {
  en_attente: "badge-muted",
  en_attente_demarrage: "badge-muted",
  demande_validation: "badge-warn",
  reserves_deposees: "badge-warn",
  reserves_ecartees_regul: "badge-warn",
  reception_acquise: "badge-valid",
  debloque: "badge-valid",
  litige: "badge-danger",
};

/**
 * Compte les jours calendaires restants (arrondi haut) entre maintenant et une
 * deadline ISO 8601. Retourne un entier négatif si la deadline est dépassée.
 */
export function daysUntil(iso) {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  const now = Date.now();
  return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
}

/** Format court "dans 3 jours" / "dépassé de 1 jour" / "aujourd'hui". */
export function fmtCountdown(iso) {
  const d = daysUntil(iso);
  if (d === null) return "";
  if (d > 1) return `dans ${d} jours`;
  if (d === 1) return "dans 1 jour";
  if (d === 0) return "aujourd'hui";
  if (d === -1) return "dépassé d'1 jour";
  return `dépassé de ${-d} jours`;
}

/** Format long date + heure (ex : « 12/03/2026 à 23:59 »). */
export function fmtDeadline(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yyyy} à ${hh}:${mi}`;
}

/**
 * Rend un objet {tone, text, consequence} exploitable pour afficher un bloc
 * d'alerte lisible au donneur d'ordre. La tonalité passe à ``critical`` à
 * moins de 3 jours restants (correspond au dernier rappel J+6 côté serveur).
 */
export function receptionCountdownWarning(deadlineIso) {
  const d = daysUntil(deadlineIso);
  if (d === null) return null;
  const tone = d < 0 ? "expired" : d <= 2 ? "critical" : d <= 4 ? "warn" : "info";
  return {
    tone,
    days: d,
    label: fmtCountdown(deadlineIso),
    deadline: fmtDeadline(deadlineIso),
    consequence:
      d < 0
        ? "Le délai est dépassé : la réception est considérée comme acquise tacitement, les fonds seront libérés au prestataire — virement sous 2 à 4 jours ouvrés."
        : "Sans validation ni réserves recevables de votre part avant cette date, la réception sera considérée comme acquise (validation tacite) et les fonds seront libérés au prestataire — virement sous 2 à 4 jours ouvrés.",
  };
}

/**
 * Renvoie le motif écrit de la dernière décision admin (recevable/écartées)
 * sur le cycle courant. Utilisé pour l'afficher aux deux parties dans le
 * panneau du jalon — la transparence évite les contestations.
 */
export function lastAdminDecisionForCurrentCycle(jalon) {
  if (!jalon?.reserves?.length) return null;
  const cycle = jalon.cycles_count;
  const cycleReserves = jalon.reserves.filter((r) => r.cycle === cycle);
  const decided = cycleReserves.filter(
    (r) => r.admin_decision === "recevable" || r.admin_decision === "ecartees",
  );
  if (!decided.length) return null;
  // Toutes les réserves d'un cycle reçoivent la même décision (traitement en lot admin).
  const first = decided[0];
  return {
    decision: first.admin_decision,
    motif: first.admin_motif,
    admin_at: first.admin_at,
    cycle,
  };
}
